import firebase from "../../config/firebase";
import { refreshAuth } from "./authActions";

export const addTalkingBadge =
  (formData, onSuccess = () => {}) =>
  async (dispatch) => {
    try {
      let { appealingVideo, introducingVideo, ...newFormData } = formData;
      dispatch(talkingAddLoader(true));

      // Create badge document immediately with placeholder URLs
      const badgeData = {
        ...newFormData,
        introducingImageUrl: null, // Will be updated in background
        appealingVideoUrl: formData.appealingVideo instanceof File ? null : formData.appealingVideo,
        createdAt: firebase.firestore.Timestamp.now(),
      };

      const docRef = await firebase.firestore().collection("talking_badges").add(badgeData);
      const badgeId = docRef.id;

      // Show success immediately, run remaining operations in background
      dispatch(talkingAddLoader(false));
      onSuccess();

      // Background operations (don't block user experience)
      const storageRef = firebase.storage().ref();
      
      // Build background task array
      const backgroundTasks = [
        // Update badge count
        formData.type === "talkingBadges"
          ? firebase
              .firestore()
              .collection("users")
              .doc(formData?.athleteId)
              .update({ talkingBadge: firebase.firestore.FieldValue.increment(1) })
          : formData.type === "votegetter"
          ? firebase
              .firestore()
              .collection("contest_videos")
              .doc(formData?.contestVideoId)
              .update({
                voteGetterBadge: firebase.firestore.FieldValue.increment(1),
              })
          : Promise.resolve(),
        
        // Create notification
        firebase
          .firestore()
          .collection("fan_notifications")
          .add({
            createdAt: firebase.firestore.Timestamp.now(),
            athleteId: formData.athleteId,
            isRead: [],
            type: "talking_badge",
          }),
      ];

      // Add video upload tasks to background
      const videoUploadPromises = [];
      
      // Upload introducing video
      const image1Ref = storageRef.child(
        `talkingbadges/${formData.introducingVideo.name}`
      );
      videoUploadPromises.push(
        image1Ref.put(formData.introducingVideo).then(() => image1Ref.getDownloadURL())
      );

      // Upload appealing video (conditional)
      if (formData.appealingVideo instanceof File) {
        const image2Ref = storageRef.child(
          `talkingbadges/${formData.appealingVideo.name}`
        );
        videoUploadPromises.push(
          image2Ref.put(formData.appealingVideo).then(() => image2Ref.getDownloadURL())
        );
      } else {
        videoUploadPromises.push(Promise.resolve(formData.appealingVideo));
      }

      // Add upload + update task to background tasks
      backgroundTasks.push(
        Promise.all(videoUploadPromises)
          .then(([introducingImageUrl, appealingVideoUrl]) => {
            return firebase
              .firestore()
              .collection("talking_badges")
              .doc(badgeId)
              .update({
                introducingImageUrl,
                appealingVideoUrl,
              });
          })
      );
      
      Promise.all(backgroundTasks)
        .then(() => {
          // Refresh auth after background tasks complete
          dispatch(refreshAuth(formData.athleteId));
        })
        .catch((error) => {
          console.error("Background task error:", error);
        });

    } catch (error) {
      console.error("Error adding talking badge:", error);
      dispatch(talkingAddLoader(false));
    }
  };

export const getTalkingBadge =
  (uid, startAfterDocument, prevObj, onSuccess) => async (dispatch) => {
    try {
      dispatch(talkingLoader(true));

      const contests = prevObj || [];
      let query = firebase
        .firestore()
        .collection("talking_badges")
        .where("athleteId", "==", uid)
        .where("type", "==", "talkingBadges")
        .where("isDeleted", "==", false)
        .orderBy("createdAt", "desc");

      if (startAfterDocument) {
        query = query?.startAfter(startAfterDocument);
      }
      query = query.limit(5);

      const querySnapshot = await query.get();

      querySnapshot.forEach(async (doc) => {
        const docData = doc.data();
        contests.push({ id: doc.id, ...docData });
      });

      dispatch({
        type: "GET_TALKING_BADGE",
        payload: {
          contestData: contests,
          hasMoreDocuments: querySnapshot.docs.length >= 5,
          lastDocument:
            querySnapshot.docs.length > 0
              ? querySnapshot.docs[querySnapshot.docs.length - 1]
              : null,
        },
      });

      dispatch(talkingLoader(false));
      onSuccess();
    } catch (error) {
      console.error("Error getting talking badges:", error);
      dispatch(talkingLoader(false));
    }
  };

export const getSingleBrandAmbassodarContest = (docId) => async (dispatch) => {
  try {
    const docSnapshot = await firebase
      .firestore()
      .collection("contest")
      .doc(docId)
      .get();

    if (docSnapshot.exists) {
      const contestData = {
        id: docSnapshot.id,
        ...docSnapshot.data(),
      };

      dispatch({
        type: "GET_SINGLE_BRAND_AMBASSODAR_CONTEST",
        payload: contestData,
      });
    }
  } catch (error) {
    console.error(error);
  }
};

export const getSingleContestVideo = (docId, athleteID) => async (dispatch) => {
  try {
    const querySnapshot = await firebase
      .firestore()
      .collection("contest_videos")
      .where("contest_id", "==", docId)
      .where("athleteId", "==", athleteID)
      .get();

    if (!querySnapshot.empty) {
      querySnapshot.forEach((doc) => {
        const contestData = {
          id: doc.id,
          ...doc.data(),
        };

        dispatch({
          type: "GET_SINGLE_BRAND_AMBASSADOR_CONTEST_VIDEO",
          payload: contestData,
        });
      });
    }
  } catch (error) {
    console.error(error);
  }
};

export const getVoteGetterBadge = (obj, onSuccess) => async (dispatch) => {
  try {
    dispatch(talkingLoader(true));
    const unsubscribe = firebase
      .firestore()
      .collection("talking_badges")
      .where("athleteId", "==", obj.athleteId)
      .where("contestId", "==", obj.contestId)
      .onSnapshot(
        (querySnapshot) => {
          const badges = [];
          querySnapshot.forEach((doc) => {
            const badgeData = {
              id: doc.id,
              ...doc.data(),
            };
            badges.push(badgeData);
          });
          dispatch({ type: "GET_VOTEGETTER_BADGE", payload: badges });
          dispatch(talkingLoader(false));
        },
        (error) => {
          console.error("Error getting talking badges:", error);
          dispatch(talkingLoader(false));
        }
      );
    onSuccess();
    return unsubscribe;
  } catch (error) {
    console.error("Error getting talking badges:", error);
    dispatch(talkingLoader(false));
  }
};

export const updateTalkingBadge =
  (docId, formData, onSuccess) => async (dispatch) => {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        toast.error("User not logged in");
        return;
      }
      const docRef = firebase.firestore().collection("talking_badges").doc(docId);
      const doc = await docRef.get();
      if (!doc.exists) {
        toast.error("Badge not found");
        return;
      }
      if (doc.data().athleteId !== user.uid) {
        toast.error("You are not authorized to update this badge");
        return;
      }

      dispatch(talkingAddLoader(true));
      const storageRef = firebase.storage().ref();

      const badgeData = {
        amount: formData.amount,
        athleteId: formData.athleteId,
        createdAt: firebase.firestore.Timestamp.now(),
        description: formData.description,
        athleteID: formData?.athleteID,
        type: formData?.type,
        purchaseID: formData?.purchaseID || null,
      };

      if (formData.introducingVideo instanceof File) {
        const image1Ref = storageRef.child(
          `talkingbadges/${formData.introducingVideo.name}`
        );
        await image1Ref.put(formData.introducingVideo);
        badgeData.introducingImageUrl = await image1Ref.getDownloadURL();
      } else {
        badgeData.introducingImageUrl = formData.introducingVideo;
      }

      if (formData.appealingVideo instanceof File) {
        const image2Ref = storageRef.child(
          `talkingbadges/${formData.appealingVideo.name}`
        );
        await image2Ref.put(formData.appealingVideo);
        badgeData.appealingVideoUrl = await image2Ref.getDownloadURL();
      } else {
        badgeData.appealingVideoUrl = formData.appealingVideo;
      }
      await docRef.update(badgeData);

      dispatch(talkingAddLoader(false));
      onSuccess();
    } catch (error) {
      console.error(error);
      dispatch(talkingAddLoader(false));
    }
  };

export const deleteTalkingBadge =
  (id, onComplete = () => {}) =>
  async (dispatch) => {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        toast.error("User not logged in");
        onComplete();
        return;
      }
      const docRef = firebase.firestore().collection("talking_badges").doc(id);
      const doc = await docRef.get();
      if (!doc.exists) {
        toast.error("Badge not found");
        onComplete();
        return;
      }
      if (doc.data().athleteId !== user.uid) {
        toast.error("You are not authorized to delete this badge");
        onComplete();
        return;
      }

      await docRef.update({
        isDeleted: true,
      });
      onComplete();
    } catch (error) {
      console.error("Error updating data: ", error);
    }
  };

export const talkingLoader = (val) => async (dispatch) => {
  dispatch({
    type: "LOADING",
    payload: val,
  });
};

export const talkingAddLoader = (val) => async (dispatch) => {
  dispatch({
    type: "ADD_LOADING",
    payload: val,
  });
};

export const getManagerData =
  (managerEmail, onSuccess = () => {}) =>
  async (dispatch) => {
    try {
      firebase
        .firestore()
        .collection("users")
        .where("email", "==", managerEmail)
        .get()
        .then((query) => {
          if (query.empty) {
            onSuccess();
          } else {
            const user = query.docs[0].data();
            if (user.isManager === true) {
              onSuccess("success");
            } else {
              onSuccess();
            }
          }
        });
    } catch (error) {
      console.error("Error getting talking badges:", error);
      onSuccess();
    }
  };

export const updateRatingData =
  (ratingData, docId, onComplete = () => {}) =>
  async (dispatch) => {
    try {
      await firebase
        .firestore()
        .collection("talking_badges")
        .doc(docId)
        .update(ratingData);

      dispatch({
        type: "TALKING_BADGE_RATING",
        payload: docId,
      });

      onComplete();
    } catch (error) {
      console.error("Error updating rating data: ", error);
    }
  };
